import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OrgInvitesService } from '../../global-services/org-invites.service';
import { OrgInvite, CreateOrgInviteDto } from '../../global-data-models/org-invite.model';
import { Permission } from '../../global-data-models/permission.model';
import { PermissionsService } from '../../global-services/permissions.service';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';

export interface CrudOrgInvitesUiState {
  invites: OrgInvite[];
  addInviteForm: FormGroup;
  orgLevelPermissions: Permission[];
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class CrudOrgInvitesFacade {
  private uiStateSubject = new BehaviorSubject<CrudOrgInvitesUiState | null>(null);
  uiState$ = this.uiStateSubject.asObservable();
  private orgId: string | null = null;

  public getCurrentUiState(): CrudOrgInvitesUiState | null {
    return this.uiStateSubject.value;
  }

  constructor(
    private orgInvitesService: OrgInvitesService,
    private permissionsService: PermissionsService,
    private fb: FormBuilder,
    private messageService: GlobalMessageService,
    private loading: GlobalLoadingService,
  ) {}

  private initializeForm(): FormGroup {
    return this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      permission_id: [null, [Validators.required]],
    });
  }

  async load(orgId: string) {
    try {
      this.loading.show();
      this.orgId = orgId;

      const initialForm = this.initializeForm();
      // Emit an initial state with the form, so the UI can render it while data loads
      this.uiStateSubject.next({
        invites: [],
        addInviteForm: initialForm,
        orgLevelPermissions: [],
      });

      const [invites, orgLevelPermissions] = await Promise.all([this.orgInvitesService.findAllInvitesForOrg(orgId), this.permissionsService.getAllOrgLevelPermissions()]);

      const invitesWithPermissionNames = invites.map((invite) => {
        const permission = orgLevelPermissions.find((p) => p.id === invite.permission_id);
        return {
          ...invite,
          permission_displayName: permission ? permission.displayName : 'Unknown Permission',
        };
      });

      this.uiStateSubject.next({
        invites: invitesWithPermissionNames,
        addInviteForm: initialForm, // Keep the same form instance
        orgLevelPermissions,
      });
    } catch (error) {
      const message = (error as any)?.message || 'Failed to load invites';
      this.messageService.showError('Error', message);
      // Ensure UI state is updated with error
      const currentState = this.uiStateSubject.value;
      if (currentState) {
        this.uiStateSubject.next({ ...currentState, error: message, invites: [], orgLevelPermissions: [] });
      } else {
        this.uiStateSubject.next({ invites: [], addInviteForm: this.initializeForm(), orgLevelPermissions: [], error: message });
      }
    } finally {
      this.loading.hide();
    }
  }

  async createInvite(form: FormGroup) {
    if (!this.orgId) return;
    if (form.invalid) {
      this.messageService.showError('Invalid Form', 'Please fill in all required fields.');
      return;
    }

    try {
      this.loading.show();
      const dto: CreateOrgInviteDto = form.value;
      await this.orgInvitesService.createInvite(this.orgId, dto);
      this.messageService.showSuccess('Invite Sent', `Invite sent to ${dto.email}`);
      // Reset form and reload invites
      form.reset();
      // Set to pristine and untouched to remove validation errors
      Object.keys(form.controls).forEach((key) => {
        form.get(key)?.markAsPristine();
        form.get(key)?.markAsUntouched();
      });
      form.updateValueAndValidity();

      if (this.orgId) await this.load(this.orgId); // Reload to show the new invite
    } catch (error) {
      this.messageService.showError('Error', (error as any)?.message || 'Failed to send invite');
    } finally {
      this.loading.hide();
    }
  }

  async resendInvite(inviteId: string) {
    if (!this.orgId) return;
    try {
      this.loading.show();
      await this.orgInvitesService.resendInvite(this.orgId, inviteId);
      this.messageService.showSuccess('Invite Resent', 'The invitation has been resent.');
      if (this.orgId) await this.load(this.orgId); // Reload to update status or timestamp if any
    } catch (error) {
      this.messageService.showError('Error', (error as any)?.message || 'Failed to resend invite');
    } finally {
      this.loading.hide();
    }
  }

  async revokeInvite(inviteId: string) {
    if (!this.orgId) return;
    try {
      this.loading.show();
      await this.orgInvitesService.revokeInvite(this.orgId, inviteId); // revoke maps to delete
      this.messageService.showSuccess('Invite Revoked', 'The invitation has been revoked.');
      if (this.orgId) await this.load(this.orgId); // Reload to remove the invite from the list
    } catch (error) {
      this.messageService.showError('Error', (error as any)?.message || 'Failed to revoke invite');
    } finally {
      this.loading.hide();
    }
  }

  async deleteInvite(inviteId: string) {
    // This is functionally the same as revoke in the current setup
    if (!this.orgId) return;
    try {
      this.loading.show();
      await this.orgInvitesService.deleteInvite(this.orgId, inviteId);
      this.messageService.showSuccess('Invite Deleted', 'The invitation has been permanently deleted.');
      if (this.orgId) await this.load(this.orgId); // Reload to remove the invite from the list
    } catch (error) {
      this.messageService.showError('Error', (error as any)?.message || 'Failed to delete invite');
    } finally {
      this.loading.hide();
    }
  }
}
